import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Package, Plus, Search, AlertTriangle, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StockPage = ({ user }) => {
  const [materiel, setMateriel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const emptyItem = {
    nom: "",
    reference: "",
    categorie: "panneau_solaire",
    marque: "",
    quantite_stock: 0,
    quantite_alerte: 10,
    prix_achat_ht: 0,
    prix_vente_ht: 0,
    fournisseur: "",
    notes: "",
  };

  const [formData, setFormData] = useState(emptyItem);

  useEffect(() => {
    fetchMateriel();
  }, []);

  const fetchMateriel = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/materiel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMateriel(response.data);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingItem) {
        await axios.put(`${API}/materiel/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Matériel mis à jour !");
      } else {
        await axios.post(`${API}/materiel`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Matériel créé !");
      }
      setShowDialog(false);
      setEditingItem(null);
      fetchMateriel();
      setFormData(emptyItem);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingItem(item);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce matériel ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/materiel/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Matériel supprimé");
      fetchMateriel();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredMateriel = materiel.filter((m) =>
    m.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.reference?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: materiel.length,
    alerte: materiel.filter(m => m.quantite_stock <= m.quantite_alerte).length,
    valeur: materiel.reduce((sum, m) => sum + (m.quantite_stock * m.prix_achat_ht), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-8 h-8 text-red-600" />
            Gestion du Stock
          </h1>
          <p className="text-slate-600 mt-1">Gérez votre inventaire de matériel</p>
        </div>
        <Button onClick={() => { setEditingItem(null); setFormData(emptyItem); setShowDialog(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau matériel
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
            <p className="text-sm text-slate-600">Références en stock</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{stats.alerte}</div>
            <p className="text-sm text-slate-600">Alertes stock</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.valeur)}
            </div>
            <p className="text-sm text-slate-600">Valeur totale stock</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom ou référence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredMateriel.map((item) => {
          const isLowStock = item.quantite_stock <= item.quantite_alerte;
          
          return (
            <Card key={item.id} className={`hover:shadow-md transition-shadow ${isLowStock ? 'border-red-300' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Package className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-semibold">{item.nom}</h3>
                      {isLowStock && (
                        <Badge className="bg-red-500 text-white">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Stock faible
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 mt-4 text-sm">
                      <div>
                        <p className="text-slate-500">Référence</p>
                        <p className="font-medium">{item.reference}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Marque</p>
                        <p className="font-medium">{item.marque || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Stock</p>
                        <p className="font-bold text-lg">{item.quantite_stock}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Prix vente HT</p>
                        <p className="font-bold text-green-600">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.prix_vente_ht || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                      Modifier
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(item.id)}>
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredMateriel.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun matériel trouvé</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifier le matériel" : "Ajouter du matériel"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  placeholder="Ex: Panneau solaire 500W"
                />
              </div>
              <div>
                <Label>Référence *</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({...formData, reference: e.target.value})}
                  placeholder="Ex: PV-500-BLK"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <select
                  value={formData.categorie}
                  onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="panneau_solaire">Panneau Solaire</option>
                  <option value="onduleur">Onduleur</option>
                  <option value="batterie">Batterie</option>
                  <option value="structure">Structure</option>
                  <option value="cablage">Câblage</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <Label>Marque</Label>
                <Input
                  value={formData.marque}
                  onChange={(e) => setFormData({...formData, marque: e.target.value})}
                  placeholder="Ex: JA Solar"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantité en stock *</Label>
                <Input
                  type="number"
                  value={formData.quantite_stock}
                  onChange={(e) => setFormData({...formData, quantite_stock: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Seuil d'alerte</Label>
                <Input
                  type="number"
                  value={formData.quantite_alerte}
                  onChange={(e) => setFormData({...formData, quantite_alerte: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix achat HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.prix_achat_ht}
                  onChange={(e) => setFormData({...formData, prix_achat_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Prix vente HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.prix_vente_ht}
                  onChange={(e) => setFormData({...formData, prix_vente_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div>
              <Label>Fournisseur</Label>
              <Input
                value={formData.fournisseur}
                onChange={(e) => setFormData({...formData, fournisseur: e.target.value})}
                placeholder="Nom du fournisseur"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                rows={3}
                placeholder="Informations complémentaires..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-red-600 hover:bg-red-700"
                disabled={!formData.nom || !formData.reference}
              >
                {editingItem ? "Mettre à jour" : "Créer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockPage;